/**
 * GeoNeutron API Service
 * Handles API calls to GeoNeutron/Beacon Search endpoints
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with auth interceptor
const createAuthenticatedClient = () => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add authentication token
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (config.headers && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  return client;
};

const client = createAuthenticatedClient();

export interface ForensicFlagData {
  registration_id: string;
  total_signals: number;
  signals_with_flags: number;
  unique_flags: string[];
  flag_counts: Record<string, number>;
  flag_percentages: Record<string, number>;
  all_flags: string[];
}

export interface ForensicFlagsResponse {
  devices: Record<string, ForensicFlagData>;
  total_devices: number;
  query_date_range: {
    start: string;
    end: string;
  };
}

/** BS Explore API device info (from /devices/bulk or /devices) */
export interface ExploreDeviceInfo {
  registrationID?: string;
  createdOn?: string;
  updatedOn?: string;
  firstSignalSeenOn?: string;
  lastSignalSeenOn?: string;
  userAgentFull?: string | null;
  userAgentBrowserFamily?: string | null;
  userAgentBrowserVersion?: string | null;
  userAgentOSFamily?: string | null;
  userAgentOSVersion?: string | null;
  userAgentDeviceFamily?: string | null;
  userAgentDeviceBrand?: string | null;
  userAgentDeviceModel?: string | null;
  eu?: boolean;
  us?: boolean;
  lastSeenIPAddresses?: Array<{ ipAddress?: string; timestamp?: number }> | null;
}

const geoneutronApi = {
  /**
   * Query forensic flags for devices within a date range
   */
  async queryForensicFlags(
    registrationIds: string[],
    startDate: string,
    endDate: string
  ): Promise<ForensicFlagsResponse> {
    const response = await client.post('/geoneutron/forensic-flags/query', {
      registration_ids: registrationIds,
      start_date: startDate,
      end_date: endDate,
    });
    return response.data;
  },

  /**
   * Search location data for devices
   */
  async searchLocationData(
    registrationIds: string[],
    startDate: string,
    endDate: string,
    responseType: 'DIRECT' | 'EXPORT' = 'DIRECT'
  ) {
    const response = await client.post('/geoneutron/location-data/search', {
      registration_ids: registrationIds,
      start_date: startDate,
      end_date: endDate,
      response_type: responseType,
    });
    return response.data;
  },

  /**
   * Get device information
   */
  async getDeviceInfo(registrationId: string) {
    const response = await client.get('/geoneutron/devices', {
      params: { registration_id: registrationId },
    });
    return response.data;
  },

  /**
   * Get device information for multiple registration IDs (BS Explore API).
   * Batches requests in chunks of 1000 (API limit per request) and merges results.
   */
  async getDevicesBulk(
    registrationIds: string[],
    responseType: 'DIRECT' | 'EXPORT' = 'DIRECT'
  ): Promise<{ devices?: ExploreDeviceInfo[] }> {
    if (registrationIds.length === 0) return { devices: [] };
    const BATCH_SIZE = 1000;
    const batches: string[][] = [];
    for (let i = 0; i < registrationIds.length; i += BATCH_SIZE) {
      batches.push(registrationIds.slice(i, i + BATCH_SIZE));
    }
    const allDevices: ExploreDeviceInfo[] = [];
    for (const batch of batches) {
      const response = await client.post('/geoneutron/devices/bulk', {
        registration_ids: batch,
        response_type: responseType,
      });
      const devices = response.data?.devices ?? [];
      allDevices.push(...devices);
    }
    return { devices: allDevices };
  },

  /**
   * Get demographic information for registration IDs
   */
  async getDemographics(registrationIds: string[]) {
    const response = await client.post('/geoneutron/demographics/get-demographics', {
      registration_ids: registrationIds,
    });
    return response.data;
  },

  /**
   * Get hashed email addresses for registration IDs
   */
  async getEmails(registrationIds: string[]) {
    const response = await client.post('/geoneutron/demographics/get-emails', {
      registration_ids: registrationIds,
    });
    return response.data;
  },
};

export default geoneutronApi;

